// Public route: /account-access. This does not sign the user into Firebase.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./account-access.styles.scss";

const ENDPOINT =
  "https://us-central1-antlogy.cloudfunctions.net/antlogyAccountAccess";
const STORAGE_KEY = "antlogy-account-access-session";
const labels = {
  open: "Received",
  in_review: "Under review",
  accepted: "Accepted",
  rejected: "Not accepted",
  closed: "Closed",
};
const dateText = (value) =>
  value
    ? new Date(value).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "";
function restoredSession() {
  try {
    const value = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    return value?.sessionToken && Date.parse(value.expiresAt) > Date.now()
      ? value
      : null;
  } catch {
    return null;
  }
}
async function call(op, data = {}, sessionToken, signal) {
  const response = await fetch(ENDPOINT, {
    method: "POST",
    credentials: "omit",
    cache: "no-store",
    signal,
    headers: {
      "Content-Type": "application/json",
      ...(sessionToken ? { Authorization: `Bearer ${sessionToken}` } : {}),
    },
    body: JSON.stringify({ ...data, op }),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(
      result.error || "Account access is unavailable. Please try again.",
    );
    error.status = response.status;
    throw error;
  }
  return result;
}

export default function AccountAccess() {
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || "");
  const [code, setCode] = useState("");
  const [linkToken, setLinkToken] = useState(
    () => new URLSearchParams(window.location.hash.slice(1)).get("token") || "",
  );
  const [access, setAccess] = useState(() =>
    new URLSearchParams(window.location.hash.slice(1)).has("token")
      ? null
      : restoredSession(),
  );
  const [account, setAccount] = useState(null);
  const [details, setDetails] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const operation = useRef(null);
  const mounted = useRef(false);

  const clearPrivateView = useCallback(() => {
    operation.current?.abort();
    operation.current = null;
    setAccess(null);
    setAccount(null);
    setDetails("");
    setCode("");
    setBusy(false);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);
  useEffect(() => {
    mounted.current = true;
    if (new URLSearchParams(window.location.hash.slice(1)).has("token")) {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch {}
    }
    // The proof stays in memory, out of the address bar and subsequent copied URLs.
    if (window.location.hash)
      window.history.replaceState(
        window.history.state,
        "",
        window.location.pathname + window.location.search,
      );
    return () => {
      mounted.current = false;
      operation.current?.abort();
    };
  }, []);
  useEffect(() => {
    if (!access) {
      setLoading(false);
      return;
    }
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(access));
    } catch {}
    const remaining = Date.parse(access.expiresAt) - Date.now();
    const expire = () => {
      clearPrivateView();
      setMessage(
        "Your private session expired. Request a new email to continue.",
      );
    };
    if (!(remaining > 0)) {
      expire();
      return;
    }
    const timer = setTimeout(expire, remaining);
    const controller = new AbortController();
    setLoading(true);
    setAccount(null);
    call("view", {}, access.sessionToken, controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setAccount(value);
      })
      .catch((failure) => {
        if (controller.signal.aborted) return;
        if (failure.status === 401) clearPrivateView();
        setError(failure.message || "Check your connection and try again.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [access, clearPrivateView]);

  async function run(work) {
    if (operation.current || loading) return;
    const controller = new AbortController();
    operation.current = controller;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      await work(controller.signal);
    } catch (failure) {
      if (!controller.signal.aborted && mounted.current) {
        if (failure.status === 401) clearPrivateView();
        setError(failure.message || "Check your connection and try again.");
      }
    } finally {
      if (operation.current === controller) operation.current = null;
      if (mounted.current && !controller.signal.aborted) setBusy(false);
    }
  }
  const verify = (payload) =>
    run(async (signal) => {
      const next = await call("exchange", payload, null, signal);
      if (signal.aborted) return;
      setLinkToken("");
      setCode("");
      setAccess(next);
    });
  const refresh = () =>
    run(async (signal) => {
      const next = await call("view", {}, access.sessionToken, signal);
      if (!signal.aborted) setAccount(next);
    });
  const disabled = busy || loading;
  const restricted = ["suspended", "banned"].includes(account?.status);

  return (
    <main className="accountAccess">
      <div className="accountAccess__card" aria-busy={disabled}>
        <Link to="/signin" className="accountAccess__brand">
          Antlogy
        </Link>
        <h1>Account access &amp; appeals</h1>
        <p>
          Review an account restriction, ask for a review, or check the outcome.
          You do not need to sign in.
        </p>
        {error && (
          <p className="accountAccess__error" role="alert">
            {error}
          </p>
        )}
        {message && (
          <p className="accountAccess__notice" role="status">
            {message}
          </p>
        )}
        {disabled && <p role="status">Please wait…</p>}

        {!access && (
          <>
            {linkToken && (
              <section className="accountAccess__notice">
                <h2>Your email link is ready</h2>
                <p>
                  Continue to view the account associated with this link. Only
                  open links sent to your own email.
                </p>
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => verify({ token: linkToken })}
                >
                  Continue to account
                </button>
                <button
                  type="button"
                  className="accountAccess__secondary"
                  disabled={disabled}
                  onClick={() => setLinkToken("")}
                >
                  Use email and code instead
                </button>
              </section>
            )}
            <form
              onSubmit={(event) => {
                event.preventDefault();
                run(async (signal) => {
                  const result = await call(
                    "request",
                    { email: email.trim().toLowerCase() },
                    null,
                    signal,
                  );
                  if (!signal.aborted) setMessage(result.message);
                });
              }}
            >
              <label htmlFor="access-email">Account email</label>
              <input
                id="access-email"
                type="email"
                autoComplete="email"
                required
                maxLength={254}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={disabled}
              />
              <button disabled={disabled} type="submit">
                Email me a link and code
              </button>
            </form>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                verify({ email: email.trim().toLowerCase(), code });
              }}
            >
              <label htmlFor="access-code">
                Verification code from your latest email
              </label>
              <input
                id="access-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{8}"
                required
                maxLength={8}
                value={code}
                onChange={(event) =>
                  setCode(event.target.value.replace(/\D/g, "").slice(0, 8))
                }
                disabled={disabled}
              />
              <p className="accountAccess__hint">
                Enter the eight-digit code within 15 minutes. A code or link can
                be used once.
              </p>
              <button
                type="submit"
                disabled={disabled || !email.trim() || code.length !== 8}
              >
                Verify and view account
              </button>
            </form>
          </>
        )}

        {access && (
          <>
            {account && (
              <>
                <p className="accountAccess__hint">Account: {account.email}</p>
                <section className="accountAccess__notice">
                  <h2>
                    {account.status === "suspended"
                      ? "Temporarily suspended"
                      : account.status === "banned"
                        ? "Account banned"
                        : account.status === "active"
                          ? "Account access restored"
                          : "Sign-in is unavailable"}
                  </h2>
                  {restricted ? (
                    <>
                      <p className="accountAccess__preserve">
                        {account.reason ||
                          "Contact support for more information about this restriction."}
                      </p>
                      {account.status === "suspended" &&
                        account.suspendedUntil && (
                          <p>
                            Suspended until {dateText(account.suspendedUntil)}{" "}
                            (your local time).
                          </p>
                        )}
                      {account.status === "suspended" &&
                        Date.parse(account.suspendedUntil) <= Date.now() && (
                          <p>
                            The suspension period has ended. Restoration may
                            still be processing; refresh the status shortly.
                          </p>
                        )}
                    </>
                  ) : (
                    <p>
                      {account.status === "active"
                        ? "This account has no current moderation restriction. You can return to sign-in."
                        : "Please contact support@antlogy.com so we can review your account access."}
                    </p>
                  )}
                  {account.pending && (
                    <p>
                      An account change is processing. Refresh the status
                      shortly.
                    </p>
                  )}
                </section>
                {account.appeal && (
                  <section>
                    <h2>
                      Your appeal: {labels[account.appeal.status] || "Received"}
                    </h2>
                    <p className="accountAccess__hint">
                      Submitted {dateText(account.appeal.createdAt)}
                    </p>
                    <p className="accountAccess__preserve">
                      {account.appeal.details}
                    </p>
                    {account.appeal.publicResponse && (
                      <div className="accountAccess__notice">
                        <h3>Message from our team</h3>
                        <p className="accountAccess__preserve">
                          {account.appeal.publicResponse}
                        </p>
                      </div>
                    )}
                    {["open", "in_review"].includes(account.appeal.status) && (
                      <p>
                        We will email you when the status changes. Submitting an
                        appeal does not automatically restore access.
                      </p>
                    )}
                  </section>
                )}
                {account.canAppeal && (
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      run(async (signal) => {
                        const next = await call(
                          "submit",
                          { details, restrictionKey: account.restrictionKey },
                          access.sessionToken,
                          signal,
                        );
                        if (!signal.aborted) {
                          setAccount(next);
                          setDetails("");
                          setMessage(
                            "Your appeal has been received. Our team will review it.",
                          );
                        }
                      });
                    }}
                  >
                    <label htmlFor="appeal-details">
                      Explain why you would like this decision reviewed
                    </label>
                    <textarea
                      id="appeal-details"
                      rows={6}
                      minLength={10}
                      maxLength={6000}
                      required
                      value={details}
                      onChange={(event) => setDetails(event.target.value)}
                      disabled={disabled}
                    />
                    <p className="accountAccess__hint">
                      10–6,000 characters. Include relevant context; do not
                      include passwords. One appeal can be submitted for this
                      restriction.
                    </p>
                    <button
                      type="submit"
                      disabled={disabled || details.trim().length < 10}
                    >
                      Submit appeal
                    </button>
                  </form>
                )}
              </>
            )}
            <div className="accountAccess__actions">
              <button
                type="button"
                className="accountAccess__secondary"
                disabled={disabled}
                onClick={refresh}
              >
                Refresh status
              </button>
              <button
                type="button"
                className="accountAccess__secondary"
                disabled={disabled}
                onClick={() => {
                  const token = access.sessionToken;
                  clearPrivateView();
                  setLinkToken("");
                  setMessage("Private account view closed.");
                  void call("end", {}, token).catch(() => {});
                }}
              >
                Close private view
              </button>
            </div>
            <p className="accountAccess__hint">
              This private view expires after 30 minutes.
            </p>
          </>
        )}
        <footer>
          <p>
            Cannot access your email or need more help?{" "}
            <a href="mailto:support@antlogy.com">Contact support@antlogy.com</a>
            .
          </p>
          <Link to="/signin">Back to sign in</Link>
        </footer>
      </div>
    </main>
  );
}
